import Fuse, { IFuseOptions } from 'fuse.js';
import { action, autorun, computed, IReactionDisposer, observable, reaction, runInAction } from 'mobx';
import { ISelectOption, ISuggestionItem, ITagProps, scrollToElement } from 'telia-front-react';
import ErrorUtil from '../error/ErrorUtil';
import {
  BerecCategories,
  BerecTable,
  Country,
  CountrySuggestion,
  INTERNET_SERVICE_GROUP_ID,
  NO_MOBILE_INTERNET_SERVICE_AVAILABLE_ID,
  Operator,
  Package
} from '../roaming/RoamingInterfaces';
import { CommonAPI } from '../util/ApiUtil';
import { FLAG_IMAGE_BASEURL } from '../util/CountryUtil';
import { FetchStatus } from '../util/FetchStatus';
import { getFuseResults } from '../util/ObjectUtil';
import { estonianCountryMappings, russianCountryMappings } from '../util/RoamingMappings';
import LanguageStore, { Language } from './LanguageStore';
import RootStore from './RootStore';
import RouteStore from './RouteStore';
import { Segment } from './SegmentStore';

const baseBerecTable: BerecTable = {
  vasNumbers: [],
  emergencyNumbers: [],
  pwsNumbers: [],
  countryCode: '',
  numberOfVisibleResults: {
    vasNumbers: 5,
    emergencyNumbers: 5,
    pwsNumbers: 5
  }
};

class RoamingStore {
  @observable accessor countryList: { countries: Country[]; status: string } = {
    countries: [],
    status: FetchStatus.UNINITIALIZED
  };
  @observable accessor operatorList: {
    operators: Operator[];
    description: string;
    status: string;
  } = { operators: [], description: '', status: FetchStatus.UNINITIALIZED };
  @observable accessor packageList: { packages: Package[]; status: string } = {
    packages: [],
    status: FetchStatus.UNINITIALIZED
  };
  @observable accessor berecTable: BerecTable = {
    ...baseBerecTable,
    status: FetchStatus.UNINITIALIZED
  };
  @observable accessor selectedCountry: Country = null;
  @observable accessor currentCountrySuggestions: CountrySuggestion[];
  @observable accessor allCountrySuggestions: CountrySuggestion[];
  @observable accessor countrySuggestionSearchTerm: string;
  @observable accessor operatorSelectOptions: ISelectOption[] = [];
  @observable accessor selectedOperatorSelectOption: ISelectOption = null;
  @observable accessor selectedOperator: Operator = null;
  @observable accessor dailyPackageTag: ITagProps = null;
  @observable accessor weeklyPackageTag: ITagProps = null;
  @observable accessor monthlyPackageTag: ITagProps = null;

  public disposeLiveUpdate: () => void;
  public navigationReaction: IReactionDisposer = null;
  public fuzzySearch: Fuse<CountrySuggestion>;
  public readonly fuzzySearchOptions: IFuseOptions<CountrySuggestion> = {
    shouldSort: true,
    keys: ['label', 'synonym'],
    threshold: 0.3
  };

  private languageStore: LanguageStore;
  private routeStore: RouteStore;

  constructor(rootStore: RootStore) {
    this.languageStore = rootStore.languageStore;
    this.routeStore = rootStore.routeStore;
  }

  public subscribe(segment: Segment) {
    if (!this.disposeLiveUpdate) {
      this.disposeLiveUpdate = autorun(() => this.liveUpdate(this.languageStore.currentLanguage, segment));
    }

    this.navigationReaction = reaction(
      () => this.routeStore.decodedParams,
      (decodedParams) => this.reactToNavigation(decodedParams, segment)
    );
  }

  public unsubscribe() {
    if (this.disposeLiveUpdate) {
      this.disposeLiveUpdate();
      delete this.disposeLiveUpdate;
    }

    this.navigationReaction();
  }

  public reactToNavigation = (params: any, segment: Segment) => {
    if (params.isoCode && (!this.selectedCountry || params.isoCode !== this.selectedCountry.isoCode)) {
      this.selectCountryByIsoCode(params, segment);
    } else if (!params.isoCode) {
      this.deselectCountry();
    }
  };

  public selectCountryByIsoCode = (params: any, segment: Segment) => {
    const countryByIsoCode: Country = this.countryList.countries.find((country) => {
      return country.isoCode === params.isoCode;
    });
    if (countryByIsoCode) {
      runInAction(() => {
        this.countrySuggestionSearchTerm = countryByIsoCode.name;
        this.currentCountrySuggestions = getFuseResults(this.fuzzySearch.search(countryByIsoCode.name));
        this.selectCountry(countryByIsoCode, segment);
      });
    }
  };

  public liveUpdate = async (currentLang: Language, segment: Segment) => {
    await this.fetchCountries(currentLang, segment);
    const params = this.routeStore.decodedParams;
    runInAction(() => {
      if (params.isoCode) {
        this.selectCountryByIsoCode(params, segment);
      } else if (this.selectedCountry) {
        this.selectedCountry.name = this.countryList.countries.find((country) => country.isoCode === this.selectedCountry.isoCode).name;
        this.fetchCountryOperators(this.selectedCountry.isoCode, currentLang, segment);
        this.fetchCountryPackages(this.selectedCountry.isoCode, segment);
        this.fetchBerecTable(this.selectedCountry.isoCode);
      }
    });
  };

  public fetchCountries = async (lang: Language = this.languageStore.currentLanguage, segment: Segment) => {
    runInAction(() => {
      this.countryList.status = FetchStatus.FETCHING;
    });
    return CommonAPI.get(`/roaming/countries?lang=${lang}&segment=${segment}`)
      .then((response) => {
        runInAction(() => {
          this.countryList.countries = this.sortCountries(response.data.countries);
          this.allCountrySuggestions = this.countryList.countries.map((country) => this.mapCountryToSuggestionItem(country, lang));
          this.currentCountrySuggestions = this.allCountrySuggestions;
          this.fuzzySearch = new Fuse(this.allCountrySuggestions, this.fuzzySearchOptions);
          this.countryList.status = FetchStatus.FETCHED;
        });
      })
      .catch((e) => {
        ErrorUtil.pushErrorToTeliaRumAndLog(e);
        runInAction(() => {
          this.countryList.status = FetchStatus.ERROR;
        });
      });
  };

  public sortCountries = (countries: Country[]) => {
    return [].slice.call(countries).sort((a: Country, b: Country) => {
      if (a.name < b.name) {
        return -1;
      }
      if (a.name > b.name) {
        return 1;
      }
      return 0;
    });
  };

  public fetchCountryOperators = async (isoCode: string, lang: Language = this.languageStore.currentLanguage, segment: Segment) => {
    try {
      runInAction(() => {
        this.operatorList.status = FetchStatus.FETCHING;
      });
      const response = await CommonAPI.get(`/roaming/operators/${isoCode}?lang=${lang}&segment=${segment}`);
      runInAction(() => {
        this.operatorList = { ...response.data, status: FetchStatus.FETCHED };
        this.operatorSelectOptions = this.operatorList.operators.map(this.mapOperatorToSelectionItem);
        this.selectedOperatorSelectOption = this.operatorSelectOptions[0];
        this.selectedOperator = this.getSelectedOperatorFromList();
      });
    } catch (e) {
      ErrorUtil.pushErrorToTeliaRumAndLog(e);
      runInAction(() => {
        this.operatorList = {
          operators: [],
          description: '',
          status: FetchStatus.ERROR
        };
      });
    }
  };

  public fetchCountryPackages = async (isoCode: string, segment: Segment) => {
    try {
      runInAction(() => {
        this.packageList.status = FetchStatus.FETCHING;
      });
      const response = await CommonAPI.get(`/roaming/packages/${isoCode}?segment=${segment}`);
      runInAction(() => {
        this.packageList = {
          packages: response.data.packages,
          status: FetchStatus.FETCHED
        };
      });
    } catch (e) {
      ErrorUtil.pushErrorToTeliaRumAndLog(e);
      runInAction(() => {
        this.packageList = {
          packages: [],
          status: FetchStatus.ERROR
        };
      });
    }
  };

  public fetchBerecTable = async (isoCode: string) => {
    try {
      runInAction(() => {
        this.berecTable.status = FetchStatus.FETCHING;
      });
      const response = await CommonAPI.get(`/roaming/berec/${isoCode}/${this.languageStore.currentLanguage}`);
      runInAction(() => {
        this.berecTable = {
          ...response.data,
          numberOfVisibleResults: baseBerecTable.numberOfVisibleResults,
          status: FetchStatus.FETCHED
        };
      });
    } catch (e) {
      ErrorUtil.pushErrorToTeliaRumAndLog(e);
      runInAction(() => {
        this.berecTable = {
          ...baseBerecTable,
          status: FetchStatus.ERROR
        };
      });
    }
  };

  public selectCountry = async (country: Country, segment: Segment) => {
    runInAction(() => {
      this.selectedCountry = country;
    });
    await Promise.all([
      this.fetchCountryOperators(country.isoCode, this.languageStore.currentLanguage, segment),
      this.fetchCountryPackages(country.isoCode, segment),
      this.fetchBerecTable(country.isoCode)
    ]);
    scrollToElement('roaming-result', 500, 0);
  };

  public selectCountrySuggestionItem = (countrySuggestionItem: ISuggestionItem, segment: Segment) => {
    const selectedCountry: Country = this.countryList.countries.find((country) => {
      return country.isoCode === countrySuggestionItem.id;
    });
    runInAction(() => {
      this.routeStore.push(this.routeStore.pathname, {
        isoCode: selectedCountry.isoCode
      });
    });
    return this.selectCountry(selectedCountry, segment);
  };

  public handleCountryInputChange = (input: string) => {
    runInAction(() => {
      this.countrySuggestionSearchTerm = input;
      this.currentCountrySuggestions = getFuseResults(this.fuzzySearch.search(input));
    });
  };

  @computed
  get countrySuggestionItems() {
    return this.countrySuggestionSearchTerm ? this.currentCountrySuggestions.slice() : [];
  }

  public selectOperator = (selectedOperator: ISelectOption) => {
    runInAction(() => {
      this.selectedOperatorSelectOption = selectedOperator;
      this.selectedOperator = this.getSelectedOperatorFromList();
    });
  };

  public deselectCountry = () => {
    runInAction(() => {
      this.selectedCountry = null;
      this.countrySuggestionSearchTerm = '';
    });
  };

  public getPackageByShortName = (...shortNames: string[]) => {
    return this.packageList.packages.find((countryPackage) => {
      return shortNames.includes(countryPackage.serviceShortName);
    });
  };

  public selectedOperatorServiceGroupById = (id: number) => {
    const operator = this.selectedOperator;
    return operator.operatorServiceGroups.find((group) => {
      return group.id === id;
    });
  };

  public isCountriesFetching = () => {
    return [FetchStatus.UNINITIALIZED, FetchStatus.FETCHING].includes(this.countryList.status as FetchStatus);
  };

  public isBerecTableFetching = () => {
    return [FetchStatus.UNINITIALIZED, FetchStatus.FETCHING].includes(this.berecTable.status as FetchStatus);
  };

  public isMobileInternetCountry = () => {
    return this.operatorList.operators.some((operator) => {
      return operator.operatorServiceGroups.some(
        (serviceGroup) =>
          serviceGroup.id === INTERNET_SERVICE_GROUP_ID && serviceGroup.groupServices.some((service) => service.id !== NO_MOBILE_INTERNET_SERVICE_AVAILABLE_ID)
      );
    });
  };

  public getNumberOfMoreBerecNumberResults = (category: BerecCategories) => {
    return this.berecTable[category].length - this.berecTable.numberOfVisibleResults[category];
  };

  public showMoreBerecNumberResults = (category: BerecCategories) => {
    runInAction(() => {
      this.berecTable.numberOfVisibleResults[category] = this.berecTable.numberOfVisibleResults[category] + 5;
    });
  };

  private getSelectedOperatorFromList = () => {
    return this.operatorList.operators.find((operator) => {
      return operator.id === this.selectedOperatorSelectOption.value;
    });
  };

  private mapCountryToSuggestionItem = (country: Country, lang: Language): CountrySuggestion => {
    return {
      id: country.isoCode,
      label: country.name,
      synonym: lang === 'et' ? estonianCountryMappings[country.name] : russianCountryMappings[country.name],
      url: '#',
      image: {
        src: `${FLAG_IMAGE_BASEURL}${country.isoCode}`,
        role: 'presentation'
      }
    };
  };

  private mapOperatorToSelectionItem = (operator: Operator): ISelectOption => {
    return {
      value: operator.id,
      label: operator.name
    };
  };

  @action.bound
  public setDailyPackageTag(tag: ITagProps) {
    this.dailyPackageTag = tag;
  }
  @action.bound
  public setWeeklyPackageTag(tag: ITagProps) {
    this.weeklyPackageTag = tag;
  }
  @action.bound
  public setMonthlyPackageTag(tag: ITagProps) {
    this.monthlyPackageTag = tag;
  }
}

export default RoamingStore;
